import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'sonner';

import { useHistoryTracker } from '@/hooks';
import { useTrackersStore } from '@/shared';
import { Tracker as TrackerType } from '@/types';

import { HISTORY_STATUS } from '../../HistoryDiagram/constants';
import { getHistoryItemStatus } from '../../HistoryDiagram/utils';

import { Message } from './Message';

export const Tracker: FC<{ tracker: TrackerType }> = ({ tracker }) => {
  const [isActive, setIsActive] = useState(true);
  const { removeTracker } = useTrackersStore();

  const { data: history, isFetching: isHistoryFetching } = useHistoryTracker(tracker, {
    skip: !tracker.waitForCode || !isActive,
  });

  const toastId = useRef<string | number | undefined>(undefined);

  const historyNotification = useMemo(() => {
    const success = history?.find(({ code }) => code === tracker.waitForCode);

    return success || history?.[0];
  }, [history]);

  useEffect(() => {
    if (isHistoryFetching) {
      return;
    }

    const updateToast = (message: string | React.ReactNode, type?: 'success' | 'error') => {
      const toastWithType = (() => {
        switch (type) {
          case 'success':
            return toast.success;
          case 'error':
            return toast.error;
          default:
            return toast;
        }
      })();

      if (!toastId.current) {
        toastId.current = toastWithType(message, {
          onDismiss: () => {
            removeTracker(tracker);
          },
          duration: Infinity,
          closeButton: true,
        });
      } else {
        toastWithType(message, {
          id: toastId.current,
        });
      }
    };

    if (!historyNotification) {
      if (!toastId.current) {
        updateToast(<Message type={tracker.type} variant="pending" name={tracker.name} />);
      }

      return;
    }

    if (historyNotification) {
      if (getHistoryItemStatus(historyNotification.code) === HISTORY_STATUS.OK) {
        if (historyNotification.code === tracker.waitForCode) {
          setIsActive(false);

          updateToast(
            <Message type={tracker.type} variant="success" name={tracker.name} txId={historyNotification.txId} />,
            'success'
          );
        } else {
          updateToast(
            <Message
              type={tracker.type}
              variant="pending"
              name={tracker.name}
              txId={historyNotification.txId}
              message={historyNotification.message}
            />,
            'success'
          );
        }
      } else {
        setIsActive(false);

        updateToast(
          <Message
            type={tracker.type}
            variant="error"
            name={tracker.name}
            txId={historyNotification.txId}
            message={historyNotification.message}
          />,
          'error'
        );
      }
    }
  }, [isHistoryFetching, !historyNotification]);

  return null;
};
